import _classCallCheck from "/codebuild/output/src1015192178/src/node_modules/@babel/runtime/helpers/esm/classCallCheck.js";
import _createClass from "/codebuild/output/src1015192178/src/node_modules/@babel/runtime/helpers/esm/createClass.js";
import "core-js/modules/es.regexp.flags.js";

// do not edit .js files directly - edit src/index.jst
var fastDeepEqual = function equal(a, b) {
  if (a === b) return true;

  if (a && b && typeof a == 'object' && typeof b == 'object') {
    if (a.constructor !== b.constructor) return false;
    var length, i, keys;

    if (Array.isArray(a)) {
      length = a.length;
      if (length != b.length) return false;

      for (i = length; i-- !== 0;) {
        if (!equal(a[i], b[i])) return false;
      }

      return true;
    }

    if (a.constructor === RegExp) return a.source === b.source && a.flags === b.flags;
    if (a.valueOf !== Object.prototype.valueOf) return a.valueOf() === b.valueOf();
    if (a.toString !== Object.prototype.toString) return a.toString() === b.toString();
    keys = Object.keys(a);
    length = keys.length;
    if (length !== Object.keys(b).length) return false;

    for (i = length; i-- !== 0;) {
      if (!Object.prototype.hasOwnProperty.call(b, keys[i])) return false;
    }

    for (i = length; i-- !== 0;) {
      var key = keys[i];
      if (!equal(a[key], b[key])) return false;
    }

    return true;
  } // true if both NaN, false otherwise


  return a !== a && b !== b;
};
/**
 * Copyright 2019 Google LLC. All Rights Reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at.
 *
 *      Http://www.apache.org/licenses/LICENSE-2.0.
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */


var DEFAULT_ID = "__googleMapsScriptId";
/**
 * The status of the [[Loader]].
 */

var LoaderStatus;

(function (LoaderStatus) {
  LoaderStatus[LoaderStatus["INITIALIZED"] = 0] = "INITIALIZED";
  LoaderStatus[LoaderStatus["LOADING"] = 1] = "LOADING";
  LoaderStatus[LoaderStatus["SUCCESS"] = 2] = "SUCCESS";
  LoaderStatus[LoaderStatus["FAILURE"] = 3] = "FAILURE";
})(LoaderStatus || (LoaderStatus = {}));
/**
 * [[Loader]] makes it easier to add Google Maps JavaScript API to your application
 * dynamically using
 * [Promises](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise).
 * It works by dynamically creating and appending a script node to the the
 * document head and wrapping the callback function so as to return a promise.
 *
 * ```
 * const loader = new Loader({
 *   apiKey: "",
 *   version: "weekly",
 *   libraries: ["places"]
 * });
 *
 * loader.load().then((google) => {
 *   const map = new google.maps.Map(...)
 * })
 * ```
 */


var Loader = /*#__PURE__*/function () {
  /**
   * Creates an instance of Loader using [[LoaderOptions]]. No defaults are set
   * using this library, instead the defaults are set by the Google Maps
   * JavaScript API server.
   *
   * ```
   * const loader = Loader({apiKey, version: 'weekly', libraries: ['places']});
   * ```
   */
  function Loader(_ref) {
    var apiKey = _ref.apiKey,
        authReferrerPolicy = _ref.authReferrerPolicy,
        channel = _ref.channel,
        client = _ref.client,
        _ref$id = _ref.id,
        id = _ref$id === void 0 ? DEFAULT_ID : _ref$id,
        language = _ref.language,
        _ref$libraries = _ref.libraries,
        libraries = _ref$libraries === void 0 ? [] : _ref$libraries,
        mapIds = _ref.mapIds,
        nonce = _ref.nonce,
        region = _ref.region,
        _ref$retries = _ref.retries,
        retries = _ref$retries === void 0 ? 3 : _ref$retries,
        _ref$url = _ref.url,
        url = _ref$url === void 0 ? "https://maps.googleapis.com/maps/api/js" : _ref$url,
        version = _ref.version;

    _classCallCheck(this, Loader);

    this.CALLBACK = "__googleMapsCallback";
    this.callbacks = [];
    this.done = false;
    this.loading = false;
    this.errors = [];
    this.apiKey = apiKey;
    this.authReferrerPolicy = authReferrerPolicy;
    this.channel = channel;
    this.client = client;
    this.id = id || DEFAULT_ID; // Do not allow empty string

    this.language = language;
    this.libraries = libraries;
    this.mapIds = mapIds;
    this.nonce = nonce;
    this.region = region;
    this.retries = retries;
    this.url = url;
    this.version = version;

    if (Loader.instance) {
      if (!fastDeepEqual(this.options, Loader.instance.options)) {
        throw new Error("Loader must not be called again with different options. ".concat(JSON.stringify(this.options), " !== ").concat(JSON.stringify(Loader.instance.options)));
      }

      return Loader.instance;
    }

    Loader.instance = this;
  }

  _createClass(Loader, [{
    key: "options",
    get: function get() {
      return {
        version: this.version,
        apiKey: this.apiKey,
        channel: this.channel,
        client: this.client,
        id: this.id,
        libraries: this.libraries,
        language: this.language,
        region: this.region,
        mapIds: this.mapIds,
        nonce: this.nonce,
        url: this.url,
        authReferrerPolicy: this.authReferrerPolicy
      };
    }
  }, {
    key: "status",
    get: function get() {
      if (this.errors.length) {
        return LoaderStatus.FAILURE;
      }

      if (this.done) {
        return LoaderStatus.SUCCESS;
      }

      if (this.loading) {
        return LoaderStatus.LOADING;
      }

      return LoaderStatus.INITIALIZED;
    }
  }, {
    key: "failed",
    get: function get() {
      return this.done && !this.loading && this.errors.length >= this.retries + 1;
    }
    /**
     * CreateUrl returns the Google Maps JavaScript API script url given the [[LoaderOptions]].
     *
     * @ignore
     */

  }, {
    key: "createUrl",
    value: function createUrl() {
      var url = this.url;
      url += "?callback=".concat(this.CALLBACK);

      if (this.apiKey) {
        url += "&key=".concat(this.apiKey);
      }

      if (this.channel) {
        url += "&channel=".concat(this.channel);
      }

      if (this.client) {
        url += "&client=".concat(this.client);
      }

      if (this.libraries.length > 0) {
        url += "&libraries=".concat(this.libraries.join(","));
      }

      if (this.language) {
        url += "&language=".concat(this.language);
      }

      if (this.region) {
        url += "&region=".concat(this.region);
      }

      if (this.version) {
        url += "&v=".concat(this.version);
      }

      if (this.mapIds) {
        url += "&map_ids=".concat(this.mapIds.join(","));
      }

      if (this.authReferrerPolicy) {
        url += "&auth_referrer_policy=".concat(this.authReferrerPolicy);
      }

      return url;
    }
  }, {
    key: "deleteScript",
    value: function deleteScript() {
      var script = document.getElementById(this.id);

      if (script) {
        script.remove();
      }
    }
    /**
     * Load the Google Maps JavaScript API script and return a Promise.
     */

  }, {
    key: "load",
    value: function load() {
      return this.loadPromise();
    }
    /**
     * Load the Google Maps JavaScript API script and return a Promise.
     *
     * @ignore
     */

  }, {
    key: "loadPromise",
    value: function loadPromise() {
      var _this = this;

      return new Promise(function (resolve, reject) {
        _this.loadCallback(function (err) {
          if (!err) {
            resolve(window.google);
          } else {
            reject(err.error);
          }
        });
      });
    }
    /**
     * Load the Google Maps JavaScript API script with a callback.
     */

  }, {
    key: "loadCallback",
    value: function loadCallback(fn) {
      this.callbacks.push(fn);
      this.execute();
    }
    /**
     * Set the script on document.
     */

  }, {
    key: "setScript",
    value: function setScript() {
      if (document.getElementById(this.id)) {
        // TODO wrap onerror callback for cases where the script was loaded elsewhere
        this.callback();
        return;
      }

      var url = this.createUrl();
      var script = document.createElement("script");
      script.id = this.id;
      script.type = "text/javascript";
      script.src = url;
      script.onerror = this.loadErrorCallback.bind(this);
      script.defer = true;
      script.async = true;

      if (this.nonce) {
        script.nonce = this.nonce;
      }

      document.head.appendChild(script);
    }
    /**
     * Reset the loader state.
     */

  }, {
    key: "reset",
    value: function reset() {
      this.deleteScript();
      this.done = false;
      this.loading = false;
      this.errors = [];
      this.onerrorEvent = null;
    }
  }, {
    key: "resetIfRetryingFailed",
    value: function resetIfRetryingFailed() {
      if (this.failed) {
        this.reset();
      }
    }
  }, {
    key: "loadErrorCallback",
    value: function loadErrorCallback(e) {
      var _this2 = this;

      this.errors.push(e);

      if (this.errors.length <= this.retries) {
        var delay = this.errors.length * Math.pow(2, this.errors.length);
        console.log("Failed to load Google Maps script, retrying in ".concat(delay, " ms."));
        setTimeout(function () {
          _this2.deleteScript();

          _this2.setScript();
        }, delay);
      } else {
        this.onerrorEvent = e;
        this.callback();
      }
    }
  }, {
    key: "setCallback",
    value: function setCallback() {
      window.__googleMapsCallback = this.callback.bind(this);
    }
  }, {
    key: "callback",
    value: function callback() {
      var _this3 = this;

      this.done = true;
      this.loading = false;
      this.callbacks.forEach(function (cb) {
        cb(_this3.onerrorEvent);
      });
      this.callbacks = [];
    }
  }, {
    key: "execute",
    value: function execute() {
      this.resetIfRetryingFailed();

      if (this.done) {
        this.callback();
      } else {
        // short circuit and warn if google.maps is already loaded
        if (window.google && window.google.maps && window.google.maps.version) {
          console.warn("Google Maps already loaded outside @googlemaps/js-api-loader." + "This may result in undesirable behavior as options and script parameters may not match.");
          this.callback();
          return;
        }

        if (this.loading) ;else {
          this.loading = true;
          this.setCallback();
          this.setScript();
        }
      }
    }
  }]);

  return Loader;
}();

export { DEFAULT_ID, Loader, LoaderStatus };